import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon, FunnelIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

interface Unit {
  _id: string;
  name: {
    en: string;
    ar: string;
  };
  type?: 'volume' | 'weight' | 'length' | 'count' | 'other';
  active: boolean;
  order: number;
}

export default function UnitsTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadUnits();
  }, [activeSearch, typeFilter, activeFilter]);

  const loadUnits = async () => {
    try {
      setLoading(true);
      const params: any = {};
      
      if (activeSearch) params.search = activeSearch;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (activeFilter === 'active') params.activeOnly = 'true';
      if (activeFilter === 'inactive') params.activeOnly = 'false';
      
      const response = await api.get('/admin/units', { params });
      // Handle different response structures
      let unitsData = [];
      if (Array.isArray(response.data)) {
        unitsData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        unitsData = response.data.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        unitsData = response.data.data;
      }
      setUnits(unitsData);
    } catch (error) {
      console.error('Error loading units:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearch(search);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setTypeFilter('all');
    setActiveFilter('all');
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('units.confirmDelete'))) return;
    try {
      await api.delete(`/admin/units/${id}`);
      loadUnits();
    } catch (error: any) {
      console.error('Error deleting unit:', error);
      alert(error.response?.data?.message || t('units.failedToDelete'));
    }
  };

  const handleToggleActive = async (unit: Unit) => {
    try {
      await api.put(`/admin/units/${unit._id}`, { active: !unit.active });
      loadUnits();
    } catch (error: any) {
      console.error('Error updating unit:', error);
      alert(error.response?.data?.message || t('units.failedToUpdate'));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('units.title')}</h3>
            <p className="text-sm text-gray-500">{t('units.description')}</p>
          </div>
          <button
            onClick={() => navigate('/units/new')}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
          >
            <PlusIcon className="w-5 h-5" />
            <span>{t('units.addUnit')}</span>
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col space-y-4">
          <div className="flex space-x-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('units.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
            >
              {t('common.search')}
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 border rounded-lg flex items-center space-x-2 ${
                showFilters ? 'bg-gray-100 border-gray-300' : 'border-gray-300'
              }`}
            >
              <FunnelIcon className="w-5 h-5" />
              <span>{t('units.filters')}</span>
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('units.type')}</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="all">{t('units.allTypes')}</option>
                  <option value="volume">{t('units.volume')}</option>
                  <option value="weight">{t('units.weight')}</option>
                  <option value="length">{t('units.length')}</option>
                  <option value="count">{t('units.count')}</option>
                  <option value="other">{t('units.other')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('units.status')}</label>
                <select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value as any)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="all">{t('units.all')}</option>
                  <option value="active">{t('units.active')}</option>
                  <option value="inactive">{t('units.inactive')}</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('units.clearFilters')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">{t('units.loading')}</div>
        ) : units.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('units.noUnits')}</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('units.nameEnAr')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('units.type')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('units.status')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('units.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {units.map((unit) => (
                <tr key={unit._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="font-medium text-gray-900">{unit.name?.en || '-'}</div>
                    <div className="text-gray-500" dir="rtl">{unit.name?.ar || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{unit.type || 'other'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(unit)}
                      className={`px-2 py-1 text-xs rounded-full ${
                        unit.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {unit.active ? t('units.active') : t('units.inactive')}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => navigate(`/units/${unit._id}/edit`)}
                        className="text-primary-orange hover:text-orange-600"
                        title={t('common.edit')}
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(unit._id)}
                        className="text-red-600 hover:text-red-800"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

